/**
 * Marca en la navegación la sección que se está leyendo (`aria-current`), en la cabecera y
 * en el menú móvil. Un solo IntersectionObserver para todas las secciones: nada de escuchar
 * el scroll en el hilo principal.
 */
export function iniciarNavegacion(): void {
  const enlaces = Array.from(document.querySelectorAll<HTMLAnchorElement>("a.nav-enlace[href^='#']"));
  if (!enlaces.length || !("IntersectionObserver" in window)) return;

  const ids = Array.from(new Set(enlaces.map((a) => a.getAttribute("href")!.slice(1))));
  const secciones = ids.map((id) => document.getElementById(id)).filter((s): s is HTMLElement => s !== null);
  const visibilidad = new Map<string, number>();

  const marcar = (id: string | null) => {
    enlaces.forEach((a) => {
      if (id && a.getAttribute("href") === `#${id}`) a.setAttribute("aria-current", "true");
      else a.removeAttribute("aria-current");
    });
  };

  const observador = new IntersectionObserver(
    (entradas) => {
      for (const e of entradas) visibilidad.set(e.target.id, e.isIntersecting ? e.intersectionRatio : 0);
      let mejor: string | null = null;
      let maximo = 0;
      visibilidad.forEach((ratio, id) => {
        if (ratio > maximo) {
          maximo = ratio;
          mejor = id;
        }
      });
      marcar(mejor);
    },
    // Franja central de la pantalla: cuenta la sección que se está leyendo, no la que asoma.
    { rootMargin: "-35% 0px -50% 0px", threshold: [0, 0.01, 0.2, 0.5, 1] },
  );
  secciones.forEach((s) => observador.observe(s));
}

/**
 * Saltos a un ancla de la portada (#funciones, #precios…). Las secciones bajo el pliegue no
 * se maquetan hasta que se acercan (`.diferido`, ver index.css) y, mientras tanto, ocupan una
 * altura estimada: el desplazamiento suave fijaba el destino con esas estimaciones y, al
 * maquetarse las secciones por el camino, terminaba por encima o por debajo del título.
 * Antes del primer salto se maquetan todas de una vez —ya con la página cargada, cuando no
 * compite con nada— y el navegador calcula el destino con las alturas reales.
 */
export function iniciarAnclas(): void {
  if (!document.querySelector(".diferido")) return;
  const raiz = document.documentElement;
  const maquetarTodo = () => raiz.classList.add("sin-diferir");

  // Llegada con ancla (p. ej. «Planes y precios» desde una página legal).
  if (location.hash) {
    maquetarTodo();
    return;
  }

  // En captura: se ejecuta antes del salto, que es la acción por defecto del clic.
  const alPulsar = (e: MouseEvent) => {
    const enlace = (e.target as Element | null)?.closest?.("a[href*='#']");
    if (!(enlace instanceof HTMLAnchorElement) || !enlace.hash || enlace.pathname !== location.pathname) return;
    maquetarTodo();
    document.removeEventListener("click", alPulsar, true);
  };
  document.addEventListener("click", alPulsar, true);
}
